import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { DEVI_FORMS, getForm, deviImageFor } from "@/lib/deviForms";
import { useSettings } from "@/context/SettingsContext";
import { Divider } from "@/components/MotifBackground";
import ShareButton from "@/components/ShareButton";
import Seo from "@/components/Seo";
import { ArrowLeft, ArrowRight, CalendarDays, Palette, Utensils, Feather, Sparkles, ScrollText, Flame, BookOpen } from "lucide-react";

const NOTES = {
  shailaputri: { en: "Her worship steadies the mind and lays a firm foundation for the nine-night sadhana.", hi: "उनकी पूजा चित्त को स्थिर कर नौ रात्रि की साधना की दृढ़ नींव रखती है।" },
  brahmacharini: { en: "Seekers of knowledge, focus and self-control invoke her especially.", hi: "ज्ञान, एकाग्रता एवं संयम के अभिलाषी विशेष रूप से उनका आवाहन करते हैं।" },
  chandraghanta: { en: "Invoked for courage and protection from harm.", hi: "साहस एवं अनिष्ट से रक्षा हेतु उनका आवाहन किया जाता है।" },
  kushmanda: { en: "Worshipped for health, vitality and long life.", hi: "आरोग्य, ओज एवं दीर्घायु हेतु उनकी पूजा की जाती है।" },
  skandamata: { en: "Invoked for the wellbeing of children and for wisdom.", hi: "संतान के कल्याण एवं ज्ञान हेतु उनका आवाहन किया जाता है।" },
  katyayani: { en: "Traditionally worshipped for the removal of obstacles to marriage and for victory over adversity.", hi: "विवाह-बाधा निवारण एवं संकट पर विजय हेतु परम्परागत रूप से पूजित।" },
  kalaratri: { en: "Invoked for freedom from fear, negativity and unseen obstacles.", hi: "भय, नकारात्मकता एवं अदृश्य बाधाओं से मुक्ति हेतु आवाहित।" },
  mahagauri: { en: "Worshipped for purity of mind, peace and release from past faults.", hi: "चित्त-शुद्धि, शान्ति एवं पूर्व-दोषों से मुक्ति हेतु पूजित।" },
  siddhidatri: { en: "Worshipped on the final day for fulfilment, success and spiritual perfection.", hi: "अन्तिम दिन पूर्णता, सफलता एवं आध्यात्मिक सिद्धि हेतु पूजित।" },
};

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 border border-line bg-parchment/60 p-4">
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
    <div>
      <p className="overline-label text-ink-3">{label}</p>
      <p className="mt-1 text-[15px] leading-snug text-ink">{value}</p>
    </div>
  </div>
);

const Section = ({ deva, en, children, testid }) => (
  <section className="mt-14 fade-up" data-testid={testid}>
    <div className="mb-5 flex items-baseline gap-3">
      <span className="h-px w-8 bg-[var(--gold)]" />
      <h2 className="font-deva text-2xl text-saffron deva-lh">{deva}</h2>
      <span className="overline-label text-ink-3">{en}</span>
    </div>
    {children}
  </section>
);

export default function DeviFormDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { pick, isDeva } = useLang();
  const settings = useSettings();
  const form = getForm(slug);
  const idx = DEVI_FORMS.findIndex((f) => f.slug === slug);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);

  if (!form) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="devi-form-notfound">
        <p className="text-ink-2">{pick("यह स्वरूप नहीं मिला।", "This form was not found.")}</p>
        <Link to="/navratri" className="mt-4 inline-block text-saffron">{pick("नवरात्रि पर लौटें", "Back to Navratri")}</Link>
      </div>
    );
  }

  const prev = idx > 0 ? DEVI_FORMS[idx - 1] : null;
  const next = idx < DEVI_FORMS.length - 1 ? DEVI_FORMS[idx + 1] : null;
  const note = NOTES[form.slug] || { en: "", hi: "" };

  return (
    <div data-testid="devi-form-page" className="pb-24">
      <Seo
        path={`/navratri/${form.slug}`}
        type="article"
        image={deviImageFor(form, settings)}
        title={`${form.en} (${form.deva}) — Navadurga Day ${form.day} · Mantra, Dhyana & Worship`}
        description={`${form.en}, the ${["","first","second","third","fourth","fifth","sixth","seventh","eighth","ninth"][form.day]} form of Durga worshipped on day ${form.day} of Navratri. Dhyana shloka, beej mantra, significance, how to worship, bhog and the story of ${form.en}.`}
        keywords={`${form.en}, ${form.deva}, Navadurga day ${form.day}, ${form.en} mantra, ${form.en} dhyana, ${form.en} puja, Navratri day ${form.day}, nine forms of Durga`}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line" style={{ background: `linear-gradient(135deg, ${form.accent}14, var(--surface) 55%)` }}>
        <div className="mx-auto grid grid-cols-1 max-w-4xl items-center gap-8 px-5 py-14 md:grid-cols-[minmax(0,1fr)_auto] md:gap-12 md:px-8 md:py-20">
          <div>
            <Link to="/navratri" data-testid="devi-form-back" className="inline-flex items-center gap-1.5 text-sm text-ink-3 transition-colors hover:text-saffron">
              <ArrowLeft className="h-4 w-4" /> {pick("नवरात्रि पूजा", "Navratri Pooja")}
            </Link>
            <p className="mt-6 overline-label" style={{ color: form.accent }}>{pick("नवदुर्गा · दिन", "Navadurga · Day")} {form.day}</p>
            <h1 className="mt-3 font-deva text-5xl text-ink deva-lh md:text-6xl">{form.deva}</h1>
            <p className="mt-2 font-serif-latin text-2xl text-ink-2 md:text-3xl">{form.en} <span className="text-ink-3">· {form.latin}</span></p>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink-2">{pick(form.sig_hi, form.sig_en)}</p>
            <div className="mt-6"><ShareButton title={`${form.en} · Navadurga · Dharmartha`} label={pick("साझा करें", "Share")} testid="devi-form-share" /></div>
          </div>
          {form.image && (
            <div className="relative mx-auto w-full max-w-[15rem] md:max-w-[16rem]" data-testid="devi-form-portrait">
              <div className="pointer-events-none absolute -inset-2 border" style={{ borderColor: form.accent + "55" }} aria-hidden="true" />
              <div className="relative overflow-hidden border bg-[var(--parchment)] shadow-[0_16px_44px_rgba(44,38,33,0.18)]" style={{ borderColor: form.accent + "88" }}>
                <img src={deviImageFor(form, settings)} alt={`${form.en} · ${form.deva}`} className="block h-auto w-full object-cover" />
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-5 md:px-8">
        {/* Quick facts */}
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="devi-form-facts">
          <Fact icon={CalendarDays} label={pick("दिन", "Day")} value={`${pick("नवरात्रि दिन", "Navratri day")} ${form.day}`} />
          <Fact icon={Palette} label={pick("रंग", "Colour")} value={pick(form.color_hi, form.color_en)} />
          <Fact icon={Utensils} label={pick("भोग", "Bhog")} value={pick(form.bhog_hi, form.bhog_en)} />
          <Fact icon={Feather} label={pick("वाहन", "Vahana")} value={pick(form.vahana_hi, form.vahana_en)} />
        </div>

        {/* About / Significance + Story */}
        <Section deva="स्वरूप एवं महिमा" en="About & Significance" testid="devi-form-about">
          <p className="max-w-[65ch] font-serif-latin text-[17px] leading-[1.95] text-ink-2 md:text-lg">{pick(form.story_hi, form.story_en)}</p>
        </Section>

        {/* Dhyana shloka */}
        <Section deva="ध्यान श्लोक" en="Dhyana Shloka" testid="devi-form-dhyana">
          <div className="border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-6 py-6">
            <p className="whitespace-pre-line font-deva text-xl leading-[2] text-ink md:text-2xl">{form.dhyana}</p>
          </div>
          <p className="mt-3 text-xs text-ink-3">{pick("नवदुर्गा स्तोत्रम् से — इस दिन देवी के ध्यान हेतु।", "From the Navadurga Stotram — for meditating on the Devi on this day.")}</p>
        </Section>

        {/* Beej mantra */}
        <Section deva="बीज मंत्र" en="Beej Mantra" testid="devi-form-mantra">
          <div className="border-l-2 border-saffron bg-[var(--surface-alt)]/50 px-6 py-5">
            <p className="font-deva text-2xl leading-[1.9] text-ink">{form.beej}</p>
            <p className="mt-1.5 font-serif-latin italic text-ink-2">{form.beej_iast}</p>
          </div>
        </Section>

        {/* How to worship */}
        <Section deva="पूजा विधि" en="How to Worship" testid="devi-form-worship">
          <ol className="space-y-3 text-[17px] leading-relaxed text-ink-2">
            <li className="flex gap-3"><span className="font-serif-latin text-gold">1.</span><span>{pick(`नवरात्रि के ${form.day} दिन घटस्थापना एवं नित्य दुर्गा पूजा के पश्चात् ${form.deva} का स्मरण करें।`, `On day ${form.day} of Navratri, after Ghatasthapana and the daily Durga puja, turn to ${form.en}.`)}</span></li>
            <li className="flex gap-3"><span className="font-serif-latin text-gold">2.</span><span>{pick("ऊपर दिए ध्यान श्लोक से देवी का ध्यान करें।", "Meditate on the Devi with the dhyana shloka given above.")}</span></li>
            <li className="flex gap-3"><span className="font-serif-latin text-gold">3.</span><span>{pick(`बीज मंत्र — ${form.beej} — का 11, 21 अथवा 108 बार जप करें।`, `Chant the beej mantra — ${form.beej_iast} — 11, 21 or 108 times.`)}</span></li>
            <li className="flex gap-3"><span className="font-serif-latin text-gold">4.</span><span>{pick(`${form.bhog_hi} का भोग अर्पित करें एवं परम्परा अनुसार ${form.color_hi} वस्त्र धारण करें।`, `Offer ${form.bhog_en} as bhog and, by tradition, wear ${form.color_en}.`)}</span></li>
            <li className="flex gap-3"><span className="font-serif-latin text-gold">5.</span><span>{pick("दुर्गा सप्तशती/स्तोत्र का पाठ कर आरती एवं क्षमा-प्रार्थना करें।", "Recite the Durga Saptashati / stotras, then perform the aarti and the kshama prayer.")}</span></li>
          </ol>
          <p className="mt-5 border-l-2 border-[var(--gold)]/50 bg-parchment/60 px-5 py-3 text-[15px] italic text-ink-2">{pick(note.hi, note.en)}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/rituals/navratri-durga-puja-vidhi" data-testid="devi-form-pujavidhi" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]"><Flame className="h-4 w-4" /> {pick("पूर्ण दुर्गा पूजा विधि", "Full Durga Pooja Vidhi")}</Link>
            <Link to="/kathas/durga-saptashati" data-testid="devi-form-saptashati" className="inline-flex items-center gap-2 border border-line px-5 py-2.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink"><ScrollText className="h-4 w-4" /> {pick("दुर्गा सप्तशती", "Durga Saptashati")}</Link>
          </div>
        </Section>

        <Divider className="my-16" />

        {/* Prev / Next form */}
        <div className="grid gap-4 sm:grid-cols-2" data-testid="devi-form-nav">
          {prev ? (
            <button onClick={() => navigate(`/navratri/${prev.slug}`)} data-testid="devi-form-prev" className="group flex items-center gap-3 border border-line bg-surface p-5 text-left transition-colors hover:border-[var(--gold)]">
              <ArrowLeft className="h-4 w-4 shrink-0 text-ink-3 transition-transform group-hover:-translate-x-0.5 group-hover:text-saffron" />
              <div>
                <p className="overline-label text-ink-3">{pick("पूर्व · दिन", "Previous · Day")} {prev.day}</p>
                <p className={`mt-0.5 text-lg text-ink ${isDeva ? "font-deva deva-lh" : "font-serif-latin"}`}>{pick(prev.deva, prev.en)}</p>
              </div>
            </button>
          ) : <span />}
          {next && (
            <button onClick={() => navigate(`/navratri/${next.slug}`)} data-testid="devi-form-next" className="group flex items-center justify-end gap-3 border border-line bg-surface p-5 text-right transition-colors hover:border-[var(--gold)]">
              <div>
                <p className="overline-label text-ink-3">{pick("अगला · दिन", "Next · Day")} {next.day}</p>
                <p className={`mt-0.5 text-lg text-ink ${isDeva ? "font-deva deva-lh" : "font-serif-latin"}`}>{pick(next.deva, next.en)}</p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-saffron" />
            </button>
          )}
        </div>

        {/* All nine forms */}
        <Section deva="नौ स्वरूप" en="All nine forms" testid="devi-form-all">
          <div className="flex flex-wrap gap-2">
            {DEVI_FORMS.map((f) => (
              <Link key={f.slug} to={`/navratri/${f.slug}`} className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-sm transition-colors ${f.slug === slug ? "border-[var(--gold)] bg-[rgba(184,134,11,0.08)] text-ink" : "border-line text-ink-2 hover:border-[var(--gold)]"}`}>
                <span className="font-serif-latin text-xs text-gold">{f.day}</span>
                <span className={isDeva ? "font-deva deva-lh" : ""}>{pick(f.deva, f.en)}</span>
              </Link>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
